import { useState, useEffect } from 'react';
import { Loader2, Trophy, Users, Building2, Network } from 'lucide-react';
import TopPositionTable from '../components/top-position/TopPositionTable';
import TopAgencyTable from '../components/top-position/TopAgencyTable';
import DirectionTable from '../components/top-position/DirectionTable';
import { useNegotiatorStats } from '../hooks/useNegotiatorStats';
import { useAgencyStats } from '../hooks/useAgencyStats';
import { useDirectionStats } from '../hooks/useDirectionStats';
import FiscalYearSelector from '../components/FiscalYearSelector';
import { useAuth } from '../contexts/AuthContext';

type TabType = 'negotiators' | 'agencies' | 'direction';

export default function TopPositionNego() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const isDirector = user?.role === 'direction';
  const isReadOnly = user?.role === 'lecture';

  const [activeTab, setActiveTab] = useState<TabType>('negotiators');
  const [fiscalYear, setFiscalYear] = useState<number>(() => {
    const today = new Date();
    const month = today.getMonth() + 1;
    return month <= 4 ? today.getFullYear() - 1 : today.getFullYear();
  });
  const [period, setPeriod] = useState<'year' | 'quarter' | 'month'>('year');
  const [selectedPeriod, setSelectedPeriod] = useState('1');
  const { negotiators, loading: negotiatorsLoading, error: negotiatorsError, loadData: loadNegotiatorData } = useNegotiatorStats();
  const { agencies, loading: agenciesLoading, error: agenciesError, loadData: loadAgencyData } = useAgencyStats();
  const { directors, loading: directorsLoading, error: directorsError, loadData: loadDirectionData } = useDirectionStats();
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  const getDateRange = () => {
    let startDate: string;
    let endDate: string;

    if (period === 'year') {
      startDate = `${fiscalYear}-04-01`;
      endDate = `${fiscalYear + 1}-03-31`;
    } else if (period === 'quarter') {
      const quarterMap = {
        '1': { start: '04', end: '06', year: fiscalYear },
        '2': { start: '07', end: '09', year: fiscalYear },
        '3': { start: '10', end: '12', year: fiscalYear },
        '4': { start: '01', end: '03', year: fiscalYear + 1 }
      };
      const quarter = quarterMap[selectedPeriod as keyof typeof quarterMap];
      if (!quarter) {
        const fallbackQuarter = quarterMap['1'];
        startDate = `${fallbackQuarter.year}-${fallbackQuarter.start}-01`;
        endDate = `${fallbackQuarter.year}-${fallbackQuarter.end}-${new Date(fallbackQuarter.year, parseInt(fallbackQuarter.end), 0).getDate()}`;
      } else {
        startDate = `${quarter.year}-${quarter.start}-01`;
        endDate = `${quarter.year}-${quarter.end}-${new Date(quarter.year, parseInt(quarter.end), 0).getDate()}`;
      }
    } else {
      const month = selectedPeriod.padStart(2, '0');
      const year = parseInt(month) <= 3 ? fiscalYear + 1 : fiscalYear;
      startDate = `${year}-${month}-01`;
      endDate = `${year}-${month}-${new Date(year, parseInt(month), 0).getDate()}`;
    }

    return { startDate, endDate };
  };

  useEffect(() => {
    const { startDate, endDate } = getDateRange();
    const loadData = async () => {
      if (isAdmin || isDirector || isReadOnly) {
        await Promise.all([
          loadNegotiatorData(startDate, endDate),
          loadAgencyData(startDate, endDate),
          loadDirectionData(startDate, endDate)
        ]);
      }
    };
    loadData();
  }, [fiscalYear, period, selectedPeriod, isAdmin, isDirector, isReadOnly]);

  const handlePeriodChange = (year: number, newPeriod: 'year' | 'quarter' | 'month', newSelectedPeriod: string) => {
    setFiscalYear(year);
    if (newPeriod !== period) {
      if (newPeriod === 'month') {
        const today = new Date();
        const currentMonth = today.getMonth() + 1;
        const defaultMonth = currentMonth >= 4 ? String(currentMonth).padStart(2, '0') : '04';
        setPeriod(newPeriod);
        setSelectedPeriod(defaultMonth);
      } else if (newPeriod === 'quarter') {
        setPeriod(newPeriod);
        setSelectedPeriod('1');
      } else {
        setPeriod(newPeriod);
        setSelectedPeriod('1');
      }
    } else {
      setPeriod(newPeriod);
      setSelectedPeriod(newSelectedPeriod);
    }
  };

  const loading = negotiatorsLoading || agenciesLoading || directorsLoading;
  const error = negotiatorsError || agenciesError || directorsError;

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-3">
        <Loader2 className="w-8 h-8 text-indigo-500 animate-spin" />
        <p className="text-sm text-slate-500">Chargement du classement…</p>
      </div>
    );
  }

  const { startDate, endDate } = getDateRange();

  const TABS = [
    { id: 'negotiators' as TabType, label: 'Par Négociateur', Icon: Users },
    { id: 'agencies'    as TabType, label: 'Par Agence',       Icon: Building2 },
    { id: 'direction'   as TabType, label: 'Par Direction',    Icon: Network },
  ];

  return (
    <div className="space-y-6">

      {/* ── Header ── */}
      <div className="relative rounded-2xl overflow-hidden bg-gradient-to-r from-slate-800 via-slate-900 to-indigo-950 shadow-lg">
        <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-500/10 rounded-full -translate-y-1/2 translate-x-1/3 pointer-events-none" />
        <div className="absolute bottom-0 right-32 w-40 h-40 bg-amber-500/10 rounded-full translate-y-1/2 pointer-events-none" />

        {/* ── Title row ── */}
        <div className="relative px-7 pt-6 pb-4 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-white/10 border border-white/15 flex items-center justify-center shrink-0">
              <Trophy size={22} className="text-amber-300" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white tracking-tight">Classement</h1>
              <p className="text-sm text-slate-400 mt-0.5">
                <span className="text-indigo-300 font-semibold">{negotiators.length}</span>
                {' '}négociateur{negotiators.length !== 1 ? 's' : ''} · {fiscalYear}–{fiscalYear + 1}
              </p>
            </div>
          </div>
          <div className="overflow-x-auto">
            <FiscalYearSelector
              value={fiscalYear}
              period={period}
              selectedPeriod={selectedPeriod}
              onChange={handlePeriodChange}
              variant="dark"
            />
          </div>
        </div>

        {/* ── Tab bar (inside header) ── */}
        {(isAdmin || isDirector || isReadOnly) && (
          <div className="relative px-7 pb-4 flex items-center gap-1 border-t border-white/10 pt-3">
            {TABS.map(({ id, label, Icon }) => (
              <button
                key={id}
                onClick={() => setActiveTab(id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all ${
                  activeTab === id
                    ? 'bg-white text-slate-800 shadow-sm'
                    : 'text-white/55 hover:text-white hover:bg-white/10'
                }`}
              >
                <Icon size={15} />
                {label}
              </button>
            ))}
          </div>
        )}
      </div>

      {(error || exportError) && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-red-700 text-sm">
          {error || exportError}
        </div>
      )}

      {activeTab === 'negotiators' && (
        <TopPositionTable
          negotiators={negotiators}
          period={period}
          selectedPeriod={selectedPeriod}
          fiscalYear={fiscalYear}
        />
      )}

      {activeTab === 'agencies' && (
        <TopAgencyTable
          agencies={agencies}
          startDate={startDate}
          endDate={endDate}
          period={period}
        />
      )}

      {activeTab === 'direction' && (
        <DirectionTable
          directors={directors}
          period={period}
          selectedPeriod={selectedPeriod}
          fiscalYear={fiscalYear}
        />
      )}
    </div>
  );
}
